import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, Edit2, Trash2, Tag, Truck, Percent, DollarSign, 
  Calendar, Users, Package, Check, X, AlertCircle, RefreshCw,
  Search, ChevronDown, ChevronUp, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPromotions = () => {
  const [activeTab, setActiveTab] = useState('discount-codes');
  const [discountCodes, setDiscountCodes] = useState([]);
  const [shippingRules, setShippingRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [modalType, setModalType] = useState('discount');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [codesRes, rulesRes] = await Promise.all([
        fetch(`${API_URL}/api/promotions/discount-codes`),
        fetch(`${API_URL}/api/promotions/shipping-rules`)
      ]);
      
      if (codesRes.ok) setDiscountCodes(await codesRes.json());
      if (rulesRes.ok) setShippingRules(await rulesRes.json());
    } catch (error) {
      toast.error('Failed to load promotions');
    }
    setLoading(false);
  };

  useEffect(() => { 
    // Immediately invoked async function for initial data fetch
    void (async () => { await fetchData(); })(); 
  }, []);

  const handleDelete = async (type, id) => {
    if (!window.confirm('Delete this promotion?')) return;
    
    const endpoint = type === 'discount' 
      ? `${API_URL}/api/promotions/discount-codes/${id}`
      : `${API_URL}/api/promotions/shipping-rules/${id}`;
    
    const res = await fetch(endpoint, { method: 'DELETE' });
    if (res.ok) { toast.success('Deleted'); fetchData(); }
  };

  const handleToggleStatus = async (type, item) => {
    const newStatus = item.status === 'active' ? 'inactive' : 'active';
    const endpoint = type === 'discount'
      ? `${API_URL}/api/promotions/discount-codes/${item.id}`
      : `${API_URL}/api/promotions/shipping-rules/${item.id}`;
    
    const res = await fetch(endpoint, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus })
    });
    
    if (res.ok) { toast.success(`${newStatus === 'active' ? 'Activated' : 'Deactivated'}`); fetchData(); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-heading font-semibold text-ivory-800">Promotions</h2>
          <p className="text-ivory-500 font-body">Manage discount codes and free shipping rules</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-ivory-200">
        <button
          onClick={() => setActiveTab('discount-codes')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'discount-codes' ? 'border-gold-600 text-gold-700' : 'border-transparent text-ivory-500'
          }`}
          data-testid="tab-discount-codes"
        >
          <Tag className="w-4 h-4 inline mr-2" /> Discount Codes ({discountCodes.length})
        </button>
        <button
          onClick={() => setActiveTab('shipping-rules')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'shipping-rules' ? 'border-gold-600 text-gold-700' : 'border-transparent text-ivory-500'
          }`}
          data-testid="tab-shipping-rules"
        >
          <Truck className="w-4 h-4 inline mr-2" /> Shipping Rules ({shippingRules.length})
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-gold-600 animate-spin" /></div>
      ) : activeTab === 'discount-codes' ? (
        <PromoList 
          items={discountCodes} 
          type="discount"
          onDelete={(id) => handleDelete('discount', id)}
          onToggle={(item) => handleToggleStatus('discount', item)}
          onEdit={(item) => { setModalType('discount'); setEditingItem(item); setShowModal(true); }}
          onCreate={() => { setModalType('discount'); setEditingItem(null); setShowModal(true); }}
        />
      ) : (
        <PromoList 
          items={shippingRules}
          type="shipping"
          onDelete={(id) => handleDelete('shipping', id)}
          onToggle={(item) => handleToggleStatus('shipping', item)}
          onEdit={(item) => { setModalType('shipping'); setEditingItem(item); setShowModal(true); }}
          onCreate={() => { setModalType('shipping'); setEditingItem(null); setShowModal(true); }}
        />
      )}

      {showModal && (
        <PromoModal
          type={modalType}
          item={editingItem}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchData(); }}
        />
      )}
    </div>
  );
};

// ============ Promo List Component ============
const PromoList = ({ items, type, onDelete, onToggle, onEdit, onCreate }) => {
  const getTargetingLabel = (item) => {
    if (item.applies_to_all) return 'All Products';
    const parts = [];
    if (item.category_ids?.length) parts.push(`${item.category_ids.length} categories`);
    if (item.subcategory_ids?.length) parts.push(`${item.subcategory_ids.length} subcategories`);
    if (item.product_ids?.length) parts.push(`${item.product_ids.length} products`);
    return parts.join(', ') || 'All Products';
  };

  const getExclusionLabel = (item) => {
    const parts = [];
    if (item.exclude_category_ids?.length) parts.push(`-${item.exclude_category_ids.length} cats`);
    if (item.exclude_subcategory_ids?.length) parts.push(`-${item.exclude_subcategory_ids.length} subcats`);
    if (item.exclude_product_ids?.length) parts.push(`-${item.exclude_product_ids.length} products`);
    return parts.join(', ');
  };

  return (
    <div className="space-y-4">
      <Button onClick={onCreate} className="btn-luxury-primary gap-2" data-testid={`create-${type}-btn`}>
        <Plus className="w-4 h-4" /> Create {type === 'discount' ? 'Discount Code' : 'Shipping Rule'}
      </Button>

      {items.length === 0 ? (
        <div className="text-center py-12 bg-ivory-100 rounded-xl border border-ivory-200">
          {type === 'discount' ? <Tag className="w-12 h-12 text-ivory-400 mx-auto mb-4" /> : <Truck className="w-12 h-12 text-ivory-400 mx-auto mb-4" />}
          <p className="text-ivory-500">No {type === 'discount' ? 'discount codes' : 'shipping rules'} yet</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {items.map((item) => (
            <div key={item.id} className="bg-ivory-100 rounded-xl border border-ivory-200 p-4" data-testid={`${type}-item-${item.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    {type === 'discount' ? (
                      <span className="font-mono text-lg font-bold text-gold-700 bg-gold-100 px-3 py-1 rounded">{item.code}</span>
                    ) : (
                      <span className="font-semibold text-ivory-800">{item.name}</span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${item.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-ivory-200 text-ivory-500'}`}>
                      {item.status}
                    </span>
                    {type === 'shipping' && <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">Priority: {item.priority}</span>}
                  </div>
                  
                  <p className="text-ivory-600 text-sm mb-2">{item.description || 'No description'}</p>
                  
                  <div className="flex flex-wrap gap-3 text-sm text-ivory-500">
                    {type === 'discount' && (
                      <>
                        {item.discount_type === 'percentage' && <span className="flex items-center gap-1"><Percent className="w-4 h-4" />{item.discount_value}% off</span>}
                        {item.discount_type === 'fixed_amount' && <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" />${item.discount_value} off</span>}
                        {item.discount_type === 'free_shipping' && <span className="flex items-center gap-1"><Truck className="w-4 h-4" />Free Shipping</span>}
                        {item.minimum_cart_amount && <span>Min: ${item.minimum_cart_amount}</span>}
                      </>
                    )}
                    {type === 'shipping' && (
                      <>
                        {item.condition_type === 'min_subtotal' && <span><DollarSign className="w-4 h-4 inline" />Cart ≥ ${item.condition_value}</span>}
                        {item.condition_type === 'min_quantity' && <span><Package className="w-4 h-4 inline" />Qty ≥ {item.condition_value}</span>}
                      </>
                    )}
                    <span className="bg-ivory-200 px-2 py-0.5 rounded text-xs">{getTargetingLabel(item)}</span>
                    {getExclusionLabel(item) && <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded text-xs">{getExclusionLabel(item)}</span>}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => onToggle(item)} className={item.status === 'active' ? 'text-green-600' : 'text-ivory-400'}>
                    {item.status === 'active' ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onEdit(item)}><Edit2 className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => onDelete(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ Product Picker Component ============
const ProductPicker = ({ selectedIds, onChange }) => {
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const searchProducts = useCallback(async (q) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/promotions/products/search?q=${encodeURIComponent(q)}&limit=50`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (expanded) searchProducts(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, search]);

  const toggleProduct = (sku) => {
    if (selectedIds.includes(sku)) {
      onChange(selectedIds.filter(id => id !== sku));
    } else {
      onChange([...selectedIds, sku]);
    }
  };

  return (
    <div className="border border-ivory-300 rounded-lg overflow-hidden">
      <button 
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2 flex items-center justify-between bg-ivory-50 hover:bg-ivory-100"
      >
        <span className="text-sm text-ivory-600">
          {selectedIds.length === 0 ? 'Select products...' : `${selectedIds.length} product(s) selected`}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="border-t border-ivory-200">
          <div className="p-2 border-b border-ivory-200">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-ivory-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, SKU..."
                className="pl-8 h-8 text-sm"
              />
            </div>
          </div>
          
          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-ivory-400" /></div>
            ) : products.length === 0 ? (
              <div className="p-4 text-center text-sm text-ivory-400">No products found</div>
            ) : (
              products.map((p) => (
                <label 
                  key={p.internal_sku} 
                  className="flex items-center gap-3 px-3 py-2 hover:bg-ivory-100 cursor-pointer border-b border-ivory-100 last:border-0"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(p.internal_sku)}
                    onChange={() => toggleProduct(p.internal_sku)}
                    className="rounded border-ivory-300"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-ivory-800 truncate">{p.name}</div>
                    <div className="text-xs text-ivory-400">{p.internal_sku} · {p.subcategory}</div>
                  </div>
                  <div className="text-sm text-ivory-500">${p.retail_price?.toFixed(2)}</div>
                </label>
              ))
            )}
          </div>
          
          {selectedIds.length > 0 && (
            <div className="p-2 border-t border-ivory-200 bg-ivory-50">
              <div className="flex flex-wrap gap-1">
                {selectedIds.map(sku => (
                  <span key={sku} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gold-100 text-gold-700 rounded text-xs">
                    {sku}
                    <button type="button" onClick={() => toggleProduct(sku)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============ Category Picker Component ============
const CategoryPicker = ({ selectedIds, onChange, type = 'category' }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (expanded && categories.length === 0) {
      setLoading(true);
      fetch(`${API_URL}/api/promotions/categories`)
        .then(res => res.json())
        .then(data => setCategories(data))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const toggleItem = (name) => {
    if (selectedIds.includes(name)) {
      onChange(selectedIds.filter(id => id !== name));
    } else {
      onChange([...selectedIds, name]);
    }
  };

  const items = type === 'category' 
    ? categories.map(c => ({ name: c.name, count: c.count }))
    : categories.flatMap(c => c.subcategories || []);

  return (
    <div className="border border-ivory-300 rounded-lg overflow-hidden">
      <button 
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2 flex items-center justify-between bg-ivory-50 hover:bg-ivory-100"
      >
        <span className="text-sm text-ivory-600">
          {selectedIds.length === 0 ? `Select ${type === 'category' ? 'categories' : 'subcategories'}...` : `${selectedIds.length} selected`}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {expanded && (
        <div className="border-t border-ivory-200 max-h-60 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-ivory-400" /></div>
          ) : items.length === 0 ? (
            <div className="p-4 text-center text-sm text-ivory-400">No items found</div>
          ) : (
            items.map((item) => (
              <label key={item.name} className="flex items-center gap-3 px-3 py-2 hover:bg-ivory-100 cursor-pointer border-b border-ivory-100 last:border-0">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(item.name)}
                  onChange={() => toggleItem(item.name)}
                  className="rounded border-ivory-300"
                />
                <span className="flex-1 text-sm text-ivory-700">{item.name}</span>
                <span className="text-xs text-ivory-400">{item.count} products</span>
              </label>
            ))
          )}
        </div>
      )}
      
      {selectedIds.length > 0 && (
        <div className="p-2 border-t border-ivory-200 bg-ivory-50">
          <div className="flex flex-wrap gap-1">
            {selectedIds.map(name => (
              <span key={name} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                {name}
                <button type="button" onClick={() => toggleItem(name)} className="hover:text-red-500"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============ Promo Modal ============
const PromoModal = ({ type, item, onClose, onSave }) => {
  const isEditing = !!item;
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  
  // Form state
  const [form, setForm] = useState({
    // Basic
    code: item?.code || '',
    name: item?.name || '',
    description: item?.description || '',
    discount_type: item?.discount_type || 'percentage',
    discount_value: item?.discount_value || 10,
    condition_type: item?.condition_type || 'min_subtotal',
    condition_value: item?.condition_value || 200,
    priority: item?.priority || 0,
    status: item?.status || 'active',
    
    // Conditions
    minimum_cart_amount: item?.minimum_cart_amount || '',
    minimum_quantity: item?.minimum_quantity || '',
    max_total_uses: item?.max_total_uses || '',
    
    // Targeting
    applies_to_all: item?.applies_to_all ?? true,
    category_ids: item?.category_ids || [],
    subcategory_ids: item?.subcategory_ids || [],
    product_ids: item?.product_ids || [],
    
    // Exclusions
    exclude_category_ids: item?.exclude_category_ids || [],
    exclude_subcategory_ids: item?.exclude_subcategory_ids || [],
    exclude_product_ids: item?.exclude_product_ids || [],
    exclude_sale_items: item?.exclude_sale_items || false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const endpoint = type === 'discount'
      ? `${API_URL}/api/promotions/discount-codes${isEditing ? `/${item.id}` : ''}`
      : `${API_URL}/api/promotions/shipping-rules${isEditing ? `/${item.id}` : ''}`;
    
    const body = type === 'discount' ? {
      code: form.code,
      description: form.description,
      discount_type: form.discount_type,
      discount_value: parseFloat(form.discount_value),
      minimum_cart_amount: form.minimum_cart_amount ? parseFloat(form.minimum_cart_amount) : null,
      minimum_quantity: form.minimum_quantity ? parseInt(form.minimum_quantity) : null,
      max_total_uses: form.max_total_uses ? parseInt(form.max_total_uses) : null,
      applies_to_all: form.applies_to_all,
      category_ids: form.category_ids,
      subcategory_ids: form.subcategory_ids,
      product_ids: form.product_ids,
      exclude_category_ids: form.exclude_category_ids,
      exclude_subcategory_ids: form.exclude_subcategory_ids,
      exclude_product_ids: form.exclude_product_ids,
      exclude_sale_items: form.exclude_sale_items,
      status: form.status,
    } : {
      name: form.name,
      description: form.description,
      condition_type: form.condition_type,
      condition_value: parseFloat(form.condition_value),
      priority: parseInt(form.priority),
      applies_to_all: form.applies_to_all,
      category_ids: form.category_ids,
      subcategory_ids: form.subcategory_ids,
      product_ids: form.product_ids,
      exclude_category_ids: form.exclude_category_ids,
      exclude_subcategory_ids: form.exclude_subcategory_ids,
      exclude_product_ids: form.exclude_product_ids,
      status: form.status,
    };

    try {
      const res = await fetch(endpoint, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        toast.success(isEditing ? 'Updated' : 'Created');
        onSave();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Error saving');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-ivory-50 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-ivory-200 flex justify-between items-center">
          <h3 className="text-xl font-heading font-semibold text-ivory-800">
            {isEditing ? 'Edit' : 'Create'} {type === 'discount' ? 'Discount Code' : 'Shipping Rule'}
          </h3>
          <button onClick={onClose} className="text-ivory-400 hover:text-ivory-600"><X className="w-5 h-5" /></button>
        </div>

        {/* Section Tabs */}
        <div className="flex gap-1 px-6 pt-4 border-b border-ivory-200">
          {['basic', 'targeting', 'exclusions'].map(section => (
            <button
              key={section}
              type="button"
              onClick={() => setActiveSection(section)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeSection === section ? 'bg-ivory-100 text-gold-700 border-t border-l border-r border-ivory-200' : 'text-ivory-500 hover:text-ivory-700'
              }`}
            >
              {section.charAt(0).toUpperCase() + section.slice(1)}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Basic Section */}
          {activeSection === 'basic' && (
            <>
              {type === 'discount' ? (
                <>
                  <div>
                    <Label>Code *</Label>
                    <Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="SAVE10" required className="font-mono" data-testid="discount-code-input" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="10% off your order" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Discount Type *</Label>
                      <select value={form.discount_type} onChange={(e) => setForm({...form, discount_type: e.target.value})} className="w-full h-10 px-3 rounded-md border border-ivory-300 bg-ivory-50" data-testid="discount-type-select">
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed_amount">Fixed Amount ($)</option>
                        <option value="free_shipping">Free Shipping</option>
                      </select>
                    </div>
                    {form.discount_type !== 'free_shipping' && (
                      <div>
                        <Label>Value *</Label>
                        <Input type="number" value={form.discount_value} onChange={(e) => setForm({...form, discount_value: e.target.value})} min="0" step="0.01" required data-testid="discount-value-input" />
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Min Cart Amount ($) <span className="text-xs text-ivory-400">(eligible items)</span></Label>
                      <Input type="number" value={form.minimum_cart_amount} onChange={(e) => setForm({...form, minimum_cart_amount: e.target.value})} min="0" step="0.01" placeholder="Optional" />
                    </div>
                    <div>
                      <Label>Max Uses</Label>
                      <Input type="number" value={form.max_total_uses} onChange={(e) => setForm({...form, max_total_uses: e.target.value})} min="1" placeholder="Unlimited" />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label>Rule Name *</Label>
                    <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Free Shipping on $200+" required data-testid="shipping-name-input" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Free shipping on orders over $200" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Condition *</Label>
                      <select value={form.condition_type} onChange={(e) => setForm({...form, condition_type: e.target.value})} className="w-full h-10 px-3 rounded-md border border-ivory-300 bg-ivory-50" data-testid="shipping-condition-select">
                        <option value="min_subtotal">Minimum Subtotal ($)</option>
                        <option value="min_quantity">Minimum Quantity</option>
                      </select>
                    </div>
                    <div>
                      <Label>{form.condition_type === 'min_subtotal' ? 'Min $ Amount' : 'Min Qty'} * <span className="text-xs text-ivory-400">(eligible items)</span></Label>
                      <Input type="number" value={form.condition_value} onChange={(e) => setForm({...form, condition_value: e.target.value})} min="0" step={form.condition_type === 'min_subtotal' ? '0.01' : '1'} required data-testid="shipping-value-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Priority</Label>
                      <Input type="number" value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})} min="0" />
                      <p className="text-xs text-ivory-400 mt-1">Higher = evaluated first</p>
                    </div>
                    <div>
                      <Label>Status</Label>
                      <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full h-10 px-3 rounded-md border border-ivory-300 bg-ivory-50">
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Targeting Section */}
          {activeSection === 'targeting' && (
            <>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-4">
                <p className="text-sm text-blue-700">
                  <strong>Scoped Promos:</strong> When targeting specific categories/products, minimum $ and quantity requirements apply only to eligible items.
                </p>
              </div>
              
              <div>
                <label className="flex items-center gap-2 mb-4">
                  <input type="checkbox" checked={form.applies_to_all} onChange={(e) => setForm({...form, applies_to_all: e.target.checked})} className="rounded border-ivory-300" />
                  <span className="font-medium text-ivory-800">Apply to All Products</span>
                </label>
              </div>
              
              {!form.applies_to_all && (
                <div className="space-y-4 pl-4 border-l-2 border-gold-300">
                  <div>
                    <Label>Categories</Label>
                    <CategoryPicker selectedIds={form.category_ids} onChange={(ids) => setForm({...form, category_ids: ids})} type="category" />
                  </div>
                  <div>
                    <Label>Subcategories</Label>
                    <CategoryPicker selectedIds={form.subcategory_ids} onChange={(ids) => setForm({...form, subcategory_ids: ids})} type="subcategory" />
                  </div>
                  <div>
                    <Label>Specific Products</Label>
                    <ProductPicker selectedIds={form.product_ids} onChange={(ids) => setForm({...form, product_ids: ids})} />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Exclusions Section */}
          {activeSection === 'exclusions' && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 rounded-lg border border-red-200 mb-4">
                <p className="text-sm text-red-700">
                  <strong>Exclusions:</strong> Items matching any exclusion will NOT be eligible for this promo, even if they match the targeting criteria.
                </p>
              </div>
              
              <div>
                <Label>Exclude Categories</Label>
                <CategoryPicker selectedIds={form.exclude_category_ids} onChange={(ids) => setForm({...form, exclude_category_ids: ids})} type="category" />
              </div>
              <div>
                <Label>Exclude Subcategories</Label>
                <CategoryPicker selectedIds={form.exclude_subcategory_ids} onChange={(ids) => setForm({...form, exclude_subcategory_ids: ids})} type="subcategory" />
              </div>
              <div>
                <Label>Exclude Products</Label>
                <ProductPicker selectedIds={form.exclude_product_ids} onChange={(ids) => setForm({...form, exclude_product_ids: ids})} />
              </div>
              
              {type === 'discount' && (
                <label className="flex items-center gap-2 mt-4">
                  <input type="checkbox" checked={form.exclude_sale_items} onChange={(e) => setForm({...form, exclude_sale_items: e.target.checked})} className="rounded border-ivory-300" />
                  <span className="text-ivory-700">Exclude Sale Items</span>
                </label>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-ivory-200">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" className="btn-luxury-primary flex-1" disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminPromotions;
